import { Toast, Widget } from 'jamespot-front-business';
import { Utils } from 'jamespot-react-components';
import jApi, {
    DavCalendarEvent,
    WIDGET_LEVEL_1,
    WIDGET_LEVEL_3,
    WidgetCalendarCalDavType,
    WidgetDisplayMode,
    WidgetWrapperLevel,
} from 'jamespot-user-api';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { officeConfigurationInitialState, OfficeConfigurationPrompt } from './components/OfficeConfigurationPrompt';
import { WidgetScroll } from './components/WidgetScroll';
import { WidgetStylePadding } from './components/WidgetStylePadding';
import { WidgetCalendarCalDavHeader } from './WidgetCalendarCalDav/WidgetCalendarCalDavHeader';

const WidgetCalendarCalDav = commonComponentsRegistry.getLazyComponent('WidgetCalendarCalDav');

export type JRCWidgetCalendarCalDavWrapperProps = {
    widget: WidgetCalendarCalDavType;
    inplace: boolean;
    mode?: WidgetDisplayMode;
    levelFallback?: (level: WidgetWrapperLevel, url: string) => ReactNode | undefined;
};

const REFRESH_INTERVAL = 60 * 1000;

export const JRCWidgetCalendarCalDavWrapper = ({
    widget,
    mode,
    levelFallback,
}: JRCWidgetCalendarCalDavWrapperProps) => {
    const { uniqid } = widget;
    const dispatch = useAppDispatch();
    const [isConfigurationValid, setIsConfigurationValid] = useState<boolean | undefined>(undefined);
    const [doInterval, setDoInterval] = useState<boolean>(false);
    const level = useAppSelector((state) => Widget.selectors.selectWidgetLevel(state, uniqid), shallowEqual);

    const [url, setUrl] = useState<string | undefined>(undefined);
    const [list, setList] = useState<DavCalendarEvent[]>([]);

    const handleLogout = useCallback(
        (err?: any, reload?: boolean) => {
            if (err && err.error === 400) {
                jApi.office.setConfiguration(officeConfigurationInitialState);
            }
            if (reload) {
                window.location.reload();
                return;
            }

            setIsConfigurationValid(false);
            dispatch(Widget.slice.actions.setWidgetState({ uniqid, initialized: true, loading: false }));
        },
        [dispatch, uniqid],
    );

    const handleLogin = useCallback(
        (isValid: boolean) => {
            setIsConfigurationValid(isValid);
            dispatch(Widget.slice.actions.setWidgetState({ uniqid, initialized: true }));
        },
        [dispatch, uniqid],
    );

    const handleLoading = useCallback(
        (loading: boolean) => dispatch(Widget.slice.actions.setWidgetState({ uniqid, loading })),
        [uniqid, dispatch],
    );

    const fetchProviderUrls = useCallback(() => {
        jApi.office
            .getProviderBaseUrls()
            .then(({ result }) => {
                setUrl(result.dav_calendar_url);
            })
            .finally(() => handleLoading(false));
    }, [handleLoading]);

    const fetchData = useCallback(() => {
        const now = new Date();
        const nextMonth = Utils.date.addMonth(new Date(now));

        jApi.office
            .davCalendarGetEvents({
                limit: 10,
                dateStart: Utils.date.formatDateTime(now),
                dateEnd: Utils.date.formatDateTime(nextMonth),
            })
            .then(({ result }) => {
                setList(result);
                setDoInterval(true);
            })
            .catch(() => {
                dispatch(Toast.actions.error({ label: 'PLUGIN_Manager_Connection_Error' }));
                setIsConfigurationValid(false);
                dispatch(Widget.slice.actions.setWidgetState({ uniqid, initialized: true, loading: false }));
            })
            .finally(() => handleLoading(false));
    }, [handleLoading, dispatch, uniqid]);

    useEffect(() => {
        if (!doInterval) return;
        if (!isConfigurationValid) return;
        if (level !== WIDGET_LEVEL_3) return;
        const interval = setInterval(fetchData, REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [isConfigurationValid, fetchData, doInterval, level]);

    useEffect(() => {
        handleLoading(true);
        jApi.office
            .isConfigurationValid()
            .then(({ result }) => handleLogin(result))
            .catch(handleLogout);
    }, [handleLogout, handleLoading, handleLogin]);

    useEffect(() => {
        if (!isConfigurationValid) return;
        if (level === WIDGET_LEVEL_1) {
            handleLoading(true);
            fetchProviderUrls();
            return;
        }
        fetchData();
    }, [isConfigurationValid, fetchProviderUrls, handleLoading, fetchData, level]);

    if (isConfigurationValid && level && level !== WIDGET_LEVEL_3 && levelFallback) {
        return <>{levelFallback(level, url ?? '')}</>;
    }

    return (
        <WidgetScroll
            header={() => {
                return (
                    <>
                        <WidgetCalendarCalDavHeader
                            uniqid={uniqid}
                            isConfigurationValid={isConfigurationValid !== undefined && isConfigurationValid === true}
                            handleLogout={() => handleLogout(undefined, true)}
                        />
                    </>
                );
            }}
            scrollable={() => {
                return (
                    <WidgetStylePadding uniqid={uniqid}>
                        {isConfigurationValid && <WidgetCalendarCalDav calendarListEvent={list} />}
                        {isConfigurationValid === undefined && <></>}
                        {isConfigurationValid !== undefined && isConfigurationValid === false && mode === 'view' && (
                            <OfficeConfigurationPrompt />
                        )}
                    </WidgetStylePadding>
                );
            }}
        />
    );
};
